"use client";

import { useTranslations } from "next-intl";
import type { ParticipantFormData } from "./registrationFlowTypes";
import { ParticipantPartner2ContactGrid } from "./ParticipantPartner2ContactGrid";
import { ParticipantPartner2DemographicsGrid } from "./ParticipantPartner2DemographicsGrid";

export interface ParticipantChampionshipPartner2SectionProps {
    participant: ParticipantFormData;
    index: number;
    errors: Record<string, string>;
    onUpdate: (data: Partial<ParticipantFormData>) => void;
}

export function ParticipantChampionshipPartner2Section({
    participant,
    index,
    errors,
    onUpdate,
}: ParticipantChampionshipPartner2SectionProps) {
    const t = useTranslations("strings.registrationFlow");

    const hasPartner2Data = !!(
        participant.partner2FirstName?.trim() ||
        participant.partner2LastName?.trim() ||
        participant.partner2Email?.trim() ||
        participant.partner2Phone?.trim() ||
        participant.partner2City?.trim() ||
        participant.partner2Country?.trim() ||
        participant.partner2ContactValue?.trim() ||
        participant.partner2Role?.trim()
    );

    const isActive = !participant.isSolo || hasPartner2Data;
    const partner2UiActive = isActive || participant.isSolo;
    const dimWhenSoloWithoutPartner =
        participant.isSolo && !hasPartner2Data;

    const partner2DisplayName = [
        participant.partner2FirstName?.trim(),
        participant.partner2LastName?.trim(),
    ]
        .filter(Boolean)
        .join(" ")
        .trim();

    return (
        <div
            className={`min-w-0 max-w-full rounded-xl bg-fest-canvas p-3 transition-opacity duration-300 sm:p-4 ${
                dimWhenSoloWithoutPartner ? "opacity-40" : "opacity-100"
            }`}
        >
            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 flex-col gap-1">
                    <h5
                        className={`text-sm font-bold uppercase tracking-wider transition-colors ${
                            partner2UiActive
                                ? "text-fest-burgundy"
                                : "text-fest-subtle-text"
                        }`}
                    >
                        {t("partner2Section")}
                        {participant.isSolo && !hasPartner2Data && (
                            <>
                                {" "}
                                <span className="text-xs font-normal normal-case">
                                    {t("partner2Optional")}
                                </span>
                            </>
                        )}
                    </h5>
                    {partner2DisplayName ? (
                        <p className="truncate text-sm font-medium text-fest-heading">
                            {partner2DisplayName}
                        </p>
                    ) : !participant.isSolo ? (
                        <p className="text-xs text-fest-subtle-text">
                            {t("partner2InviteHint")}
                        </p>
                    ) : null}
                </div>
            </div>

            <div className="space-y-1.5">
                <ParticipantPartner2DemographicsGrid
                    participant={participant}
                    index={index}
                    errors={errors}
                    isActive={isActive}
                    partner2UiActive={partner2UiActive}
                    onUpdate={onUpdate}
                />
                <ParticipantPartner2ContactGrid
                    participant={participant}
                    errors={errors}
                    isActive={isActive}
                    partner2UiActive={partner2UiActive}
                    onUpdate={onUpdate}
                />
            </div>
        </div>
    );
}
